import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
    ChevronLeft, ChevronRight, X, Download, ZoomIn, ZoomOut, 
    RotateCw, FileText, Receipt, Maximize2, Minimize2,
    Grid, List
} from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * DocumentSlideshow - Visionneuse de documents en mode diaporama
 * 
 * Permet de naviguer fluidement entre études et factures
 * avec gestes de swipe et raccourcis clavier
 */
export function DocumentSlideshow({ 
    documents = [], 
    initialIndex = 0, 
    isOpen, 
    onClose,
    onDownload 
}) {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showThumbnails, setShowThumbnails] = useState(true);
    const [touchStart, setTouchStart] = useState(null);
    const [loading, setLoading] = useState(true);

    const currentDoc = documents[currentIndex];

    // Reset state when opening
    useEffect(() => {
        if (isOpen) {
            setCurrentIndex(initialIndex);
            setZoom(1);
            setRotation(0);
            setLoading(true);
        }
    }, [isOpen, initialIndex]);

    // Keyboard navigation
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e) => {
            switch (e.key) {
                case 'ArrowLeft':
                    goToPrevious();
                    break;
                case 'ArrowRight':
                    goToNext();
                    break;
                case 'Escape':
                    onClose();
                    break;
                case '+':
                case '=':
                    handleZoomIn();
                    break;
                case '-':
                    handleZoomOut();
                    break;
                case 'r':
                    handleRotate();
                    break;
                case 'f':
                    toggleFullscreen();
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, currentIndex, documents.length]);

    const goToNext = useCallback(() => {
        if (currentIndex < documents.length - 1) {
            setCurrentIndex(prev => prev + 1);
            setZoom(1);
            setRotation(0);
            setLoading(true);
        }
    }, [currentIndex, documents.length]);

    const goToPrevious = useCallback(() => {
        if (currentIndex > 0) {
            setCurrentIndex(prev => prev - 1);
            setZoom(1);
            setRotation(0);
            setLoading(true);
        }
    }, [currentIndex]);

    const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
    const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.5));
    const handleRotate = () => setRotation(prev => (prev + 90) % 360);
    const toggleFullscreen = () => setIsFullscreen(prev => !prev);

    // Touch gestures for mobile
    const handleTouchStart = (e) => {
        setTouchStart(e.touches[0].clientX);
    };

    const handleTouchEnd = (e) => {
        if (!touchStart) return;
        
        const touchEnd = e.changedTouches[0].clientX;
        const diff = touchStart - touchEnd;

        if (Math.abs(diff) > 50) {
            if (diff > 0) {
                goToNext();
            } else {
                goToPrevious();
            }
        }
        setTouchStart(null);
    };

    const getDocumentUrl = (doc) => {
        if (!doc) return '';
        const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
        const token = localStorage.getItem('valnexo_token');
        
        if (doc.document_id) {
            // Études - ajouter le token comme query param pour l'iframe
            return `${baseUrl}/api/prospects/${doc.prospect_id}/documents/${doc.document_id}/view?token=${token}`;
        }
        if (doc.id) {
            // Attachments (factures) - ajouter le token
            return `${baseUrl}/api/attachments/${doc.id}/view?token=${token}`;
        }
        return doc.url || '';
    };

    const getDocumentType = (doc) => {
        if (!doc) return 'document';
        return doc.document_type || doc.attachment_type || 'DOCUMENT';
    };

    const isPdf = (doc) => {
        if (!doc) return false;
        const filename = doc.filename || doc.original_filename || '';
        return filename.toLowerCase().endsWith('.pdf') || doc.file_type === 'pdf';
    };

    const isImage = (doc) => {
        if (!doc) return false;
        const filename = doc.filename || doc.original_filename || '';
        return /\.(jpg|jpeg|png|gif|webp)$/i.test(filename);
    };

    if (!isOpen || documents.length === 0) return null;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent 
                className={cn(
                    "max-w-[98vw] w-full h-[95vh] p-0 gap-0 bg-slate-900 border-slate-700",
                    isFullscreen && "max-w-full h-full rounded-none"
                )}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-slate-800 border-b border-slate-700">
                    <div className="flex items-center gap-3">
                        <Badge 
                            variant="outline" 
                            className={cn(
                                "text-xs",
                                getDocumentType(currentDoc) === 'ETUDE' 
                                    ? "bg-purple-500/20 text-purple-300 border-purple-500/50"
                                    : "bg-blue-500/20 text-blue-300 border-blue-500/50"
                            )}
                        >
                            {getDocumentType(currentDoc) === 'ETUDE' ? (
                                <><FileText className="w-3 h-3 mr-1" /> Étude</>
                            ) : (
                                <><Receipt className="w-3 h-3 mr-1" /> Facture</>
                            )}
                        </Badge>
                        <span className="text-white font-medium truncate max-w-[300px]">
                            {currentDoc?.filename || currentDoc?.original_filename || 'Document'}
                        </span>
                        <span className="text-slate-400 text-sm">
                            {currentIndex + 1} / {documents.length}
                        </span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                        {/* Zoom controls */}
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-slate-300 hover:text-white hover:bg-slate-700"
                            onClick={handleZoomOut}
                        >
                            <ZoomOut className="w-4 h-4" />
                        </Button>
                        <span className="text-slate-400 text-sm min-w-[50px] text-center">
                            {Math.round(zoom * 100)}%
                        </span>
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-slate-300 hover:text-white hover:bg-slate-700"
                            onClick={handleZoomIn}
                        >
                            <ZoomIn className="w-4 h-4" />
                        </Button>
                        
                        <div className="w-px h-6 bg-slate-600 mx-2" />
                        
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-slate-300 hover:text-white hover:bg-slate-700"
                            onClick={handleRotate}
                        >
                            <RotateCw className="w-4 h-4" />
                        </Button>
                        
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-slate-300 hover:text-white hover:bg-slate-700"
                            onClick={toggleFullscreen}
                        >
                            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                        </Button>
                        
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-slate-300 hover:text-white hover:bg-slate-700"
                            onClick={() => setShowThumbnails(prev => !prev)}
                        >
                            {showThumbnails ? <List className="w-4 h-4" /> : <Grid className="w-4 h-4" />}
                        </Button>
                        
                        {onDownload && (
                            <Button 
                                variant="ghost" 
                                size="icon" 
                                className="text-slate-300 hover:text-white hover:bg-slate-700"
                                onClick={() => onDownload(currentDoc)}
                            >
                                <Download className="w-4 h-4" />
                            </Button>
                        )}
                        
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-slate-300 hover:text-white hover:bg-slate-700"
                            onClick={onClose}
                        >
                            <X className="w-4 h-4" />
                        </Button>
                    </div>
                </div>

                {/* Main content area */}
                <div className="flex flex-1 overflow-hidden">
                    {/* Document viewer */}
                    <div 
                        className="flex-1 relative flex items-center justify-center bg-slate-900 overflow-hidden"
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    >
                        {/* Navigation arrows */}
                        {currentIndex > 0 && (
                            <button
                                onClick={goToPrevious}
                                className="absolute left-4 z-10 p-3 rounded-full bg-black/50 text-white hover:bg-black/70 transition-all hover:scale-110"
                                data-testid="slideshow-prev"
                            >
                                <ChevronLeft className="w-6 h-6" />
                            </button>
                        )}
                        
                        {currentIndex < documents.length - 1 && (
                            <button
                                onClick={goToNext}
                                className="absolute right-4 z-10 p-3 rounded-full bg-black/50 text-white hover:bg-black/70 transition-all hover:scale-110"
                                data-testid="slideshow-next"
                            >
                                <ChevronRight className="w-6 h-6" />
                            </button>
                        )}

                        {/* Document display */}
                        <div 
                            className="w-full h-full flex items-center justify-center p-2"
                            style={{
                                transform: `scale(${zoom}) rotate(${rotation}deg)`,
                                transition: 'transform 0.2s ease-out'
                            }}
                        >
                            {loading && (
                                <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
                                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                                </div>
                            )}
                            
                            {isPdf(currentDoc) ? (
                                <iframe
                                    src={getDocumentUrl(currentDoc)}
                                    className="w-full h-full rounded-lg border-0"
                                    style={{ 
                                        backgroundColor: 'white',
                                        opacity: loading ? 0 : 1,
                                        transition: 'opacity 0.3s',
                                        minHeight: '80vh'
                                    }}
                                    onLoad={() => setLoading(false)}
                                    title={currentDoc?.filename || 'Document PDF'}
                                />
                            ) : isImage(currentDoc) ? (
                                <img
                                    src={getDocumentUrl(currentDoc)}
                                    alt={currentDoc?.filename || 'Document'}
                                    className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                                    style={{ opacity: loading ? 0 : 1, transition: 'opacity 0.3s' }}
                                    onLoad={() => setLoading(false)}
                                    onError={() => setLoading(false)}
                                />
                            ) : (
                                <div className="flex flex-col items-center justify-center text-slate-400">
                                    <FileText className="w-24 h-24 mb-4" />
                                    <p className="text-lg">Aperçu non disponible</p>
                                    <p className="text-sm mt-2">Téléchargez le document pour le visualiser</p>
                                    {onDownload && (
                                        <Button 
                                            variant="outline" 
                                            className="mt-4"
                                            onClick={() => onDownload(currentDoc)}
                                        >
                                            <Download className="w-4 h-4 mr-2" />
                                            Télécharger
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Thumbnails sidebar */}
                    {showThumbnails && documents.length > 1 && (
                        <div className="w-48 bg-slate-800 border-l border-slate-700 overflow-y-auto">
                            <div className="p-2 space-y-2">
                                {documents.map((doc, index) => (
                                    <button
                                        key={doc.id || doc.document_id || index}
                                        onClick={() => {
                                            setCurrentIndex(index);
                                            setZoom(1);
                                            setRotation(0);
                                            setLoading(true);
                                        }}
                                        className={cn(
                                            "w-full p-2 rounded-lg transition-all text-left",
                                            index === currentIndex 
                                                ? "bg-blue-600 ring-2 ring-blue-400" 
                                                : "bg-slate-700 hover:bg-slate-600"
                                        )}
                                    >
                                        <div className="flex items-center gap-2 mb-1">
                                            {getDocumentType(doc) === 'ETUDE' ? (
                                                <FileText className="w-4 h-4 text-purple-400" />
                                            ) : (
                                                <Receipt className="w-4 h-4 text-blue-400" />
                                            )}
                                            <span className="text-xs text-slate-300 truncate">
                                                {getDocumentType(doc)}
                                            </span>
                                        </div>
                                        <p className="text-xs text-white truncate">
                                            {doc.filename || doc.original_filename || `Document ${index + 1}`}
                                        </p>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Progress bar */}
                <div className="h-1 bg-slate-800">
                    <div 
                        className="h-full bg-blue-500 transition-all duration-300"
                        style={{ width: `${((currentIndex + 1) / documents.length) * 100}%` }}
                    />
                </div>

                {/* Keyboard shortcuts hint */}
                <div className="flex items-center justify-center gap-6 py-2 bg-slate-800 text-slate-500 text-xs">
                    <span>← → Navigation</span>
                    <span>+ - Zoom</span>
                    <span>R Rotation</span>
                    <span>F Plein écran</span>
                    <span>ESC Fermer</span>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default DocumentSlideshow;
